/**
 * chrome.ts — the "ink & brass" SVG fittings (style guide §2/§7).
 *
 * Small authored-SVG generators → data-URLs for the three brass fittings the
 * restyle uses: **corner ticks**, **bar end-caps**, and the **panel edge
 * line**. Kept in ONE module so DOM CSS (background-image) and a future Pixi
 * consumer (texture) share identical geometry — the fitting is defined once.
 *
 * Rule (§7): these are *fittings, not frames* — a tick, a cap, a single warm
 * line. No ornate borders, no beveled stone, no gold dragons. Every generator
 * defaults to `ACCENT.brassBright` and is a pure function of its options, so
 * it's memoizable and jsdom-safe (no DOM, no canvas).
 *
 * Nothing consumes these yet — Lanes B/D wire them onto the player frame,
 * bars, and zone banner. Adding the module is a visual no-op.
 */
import { ACCENT } from './colors';

const DEFAULT_COLOR = ACCENT.brassBright;

/** A memoized cache of generated data-URLs, keyed by the call signature. */
const dataUrlCache = new Map<string, string>();

/**
 * Encode an SVG source string as a base64 `data:image/svg+xml` URL. Collapses
 * insignificant whitespace first. btoa in the browser, Buffer under Node/jsdom
 * — same idiom as `effects/nature/natureTextures.ts`.
 */
export function svgToDataUrl(svg: string): string {
  const src = svg.replace(/\n\s*/g, ' ').trim();
  const b64 =
    typeof btoa !== 'undefined'
      ? btoa(unescape(encodeURIComponent(src)))
      : Buffer.from(src).toString('base64');
  return 'data:image/svg+xml;base64,' + b64;
}

function memo(key: string, make: () => string): string {
  const hit = dataUrlCache.get(key);
  if (hit) return hit;
  const url = make();
  dataUrlCache.set(key, url);
  return url;
}

export interface CornerTickOptions {
  /** Stroke color (default brass-bright). */
  color?: string;
  /** Square viewport size in px (default 10). */
  size?: number;
  /** Stroke width in px (default 1.5). */
  weight?: number;
}

/**
 * An L-shaped corner bracket — the top-left orientation. Rotate via CSS
 * `transform: rotate()` (or `scaleX/Y(-1)`) for the other three corners so
 * one asset serves all four. Used sparingly: player frame, zone banner, boss
 * bar (§2 — not on every panel).
 */
export function cornerTick(opts: CornerTickOptions = {}): string {
  const { color = DEFAULT_COLOR, size = 10, weight = 1.5 } = opts;
  const o = weight / 2; // inset so the stroke sits fully inside the viewport
  return memo(`tick:${color}:${size}:${weight}`, () =>
    svgToDataUrl(
      `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
        <path d="M${o} ${size} L${o} ${o} L${size} ${o}" fill="none"
          stroke="${color}" stroke-width="${weight}" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`,
    ),
  );
}

export interface BarEndCapOptions {
  /** Cap color (default brass-bright). */
  color?: string;
  /** Cap width in px (default 4). */
  width?: number;
  /** Cap height in px (default 12, the mock bar height). */
  height?: number;
}

/**
 * A soft vertical brass gloss for the leading edge of a resource bar's fill —
 * "end-cap subtly brighter" (§2). A thin rounded highlight that fades top and
 * bottom, laid over the fill's right edge; the bar's semantic color is
 * untouched (§7 — fittings never repaint bars).
 */
export function barEndCap(opts: BarEndCapOptions = {}): string {
  const { color = DEFAULT_COLOR, width = 4, height = 12 } = opts;
  return memo(`endcap:${color}:${width}:${height}`, () =>
    svgToDataUrl(
      `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stop-color="${color}" stop-opacity="0"/>
            <stop offset="0.5" stop-color="${color}" stop-opacity="0.75"/>
            <stop offset="1" stop-color="${color}" stop-opacity="0"/>
          </linearGradient>
        </defs>
        <rect x="0" y="0" width="${width}" height="${height}" rx="${width / 2}" fill="url(#g)"/>
      </svg>`,
    ),
  );
}

export interface EdgeLineOptions {
  /** Line color (default brass-bright). */
  color?: string;
  /** Line length in px (default 100 — scale via CSS background-size). */
  length?: number;
  /** Line thickness in px (default 2). */
  thickness?: number;
}

/**
 * The panel edge accent — a single warm horizontal line that fades in and out
 * at the ends (the mock's `.pframe::before`). The one "fitting" that reads as
 * crafted rather than dev-tool; used on the flagship surfaces only (§2). As an
 * SVG (not a CSS gradient) so the identical geometry is available to Pixi.
 */
export function panelEdgeLine(opts: EdgeLineOptions = {}): string {
  const { color = DEFAULT_COLOR, length = 100, thickness = 2 } = opts;
  return memo(`edge:${color}:${length}:${thickness}`, () =>
    svgToDataUrl(
      `<svg width="${length}" height="${thickness}" viewBox="0 0 ${length} ${thickness}" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="g" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stop-color="${color}" stop-opacity="0"/>
            <stop offset="0.3" stop-color="${color}" stop-opacity="1"/>
            <stop offset="0.7" stop-color="${color}" stop-opacity="1"/>
            <stop offset="1" stop-color="${color}" stop-opacity="0"/>
          </linearGradient>
        </defs>
        <rect x="0" y="0" width="${length}" height="${thickness}" rx="${thickness / 2}" fill="url(#g)"/>
      </svg>`,
    ),
  );
}

/** Grouped export for ergonomic import: `import { CHROME } from '@/game/ui/theme'`. */
export const CHROME = { cornerTick, barEndCap, panelEdgeLine, svgToDataUrl } as const;
